import { useState, useRef, useEffect } from "react";
import {
  Camera,
  Upload,
  History,
  AlertTriangle,
  CheckCircle,
  Clock,
  Leaf,
  Filter,
  X,
  Loader2,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { supabase } from "../lib/supabaseClient";
import { analyzeImage } from "../lib/imageAnalysis";
import type { User } from "../types";

interface CropHealthProps {
  user: User | null;
  onSignIn?: () => void;
}

interface AnalysisHistoryItem {
  id: string;
  status: "healthy" | "diseased" | "pest_detected" | "nutrient_deficiency";
  confidence: number;
  issues: string[];
  recommendations: string[];
  timestamp: Date;
  image_path?: string;
  provider?: string;
}

export default function CropHealth({ user, onSignIn }: CropHealthProps) {
  const { t, language } = useLanguage();
  const [tab, setTab] = useState<"upload" | "camera">("upload");
  const [image, setImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<AnalysisHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyFilter, setHistoryFilter] = useState<
    "3" | "6" | "9" | "12" | "all"
  >("3");
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [showResults, setShowResults] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  // Camera functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraError(null);
      setCameraActive(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (error) {
      setCameraError(
        "Unable to access camera. Please check browser permissions."
      );
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      const tracks = (videoRef.current.srcObject as MediaStream).getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  };

  const handleTakePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas
      .getContext("2d")!
      .drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], "captured.jpg", { type: "image/jpeg" });
        setImage(file);
        setError(null);
        setPreviewUrl(URL.createObjectURL(file));
        setTab("upload");
        stopCamera();
      }
    }, "image/jpeg");
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    setImage(file ?? null);
    if (file) {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }
  };

  const performAnalysis = async () => {
    setError(null);

    if (!user || !user.id) {
      setError("You must be signed in to analyze crops.");
      return;
    }
    if (!image) {
      setError(
        "No image selected for analysis. Please upload or capture a photo."
      );
      return;
    }

    if (image.size > 5 * 1024 * 1024) {
      setError("Image too large. Please use an image smaller than 5MB.");
      return;
    }

    setLoading(true);

    try {
      const result = await analyzeImage(image, language);

      // Save to database
      if (result.confidence > 0.6) {
        try {
          const { error: dbError } = await supabase
            .from("crop_analyses")
            .insert([
              {
                user_id: user.id,
                disease: result.status,
                confidence: Math.round(result.confidence * 100),
                severity: "Medium", // Default severity
                treatment: result.recommendations.join(". "),
                symptoms: result.issues,
                prevention: "Regular monitoring and proper care",
                image_url: null,
              },
            ]);

          if (!dbError) {
            loadAnalysisHistory();
          }
        } catch (saveError) {
          console.warn("Failed to save analysis:", saveError);
        }
      }

      // Show analysis results
      setAnalysisResult(result);
      setShowResults(true);
    } catch (e: any) {
      console.error("Analysis failed:", e);

      if (e.message?.includes("rate limit")) {
        setError("Too many requests. Please wait a moment and try again.");
      } else if (e.message?.includes("network")) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("Analysis failed. Please try again with a clearer image.");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAnalysisHistory = async () => {
    if (!user?.id) return;

    setLoadingHistory(true);

    try {
      const limit = historyFilter === "all" ? 1000 : parseInt(historyFilter);
      const { data, error } = await supabase
        .from("crop_analyses")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) {
        console.error("Failed to load history:", error);
        setHistory([]);
      } else {
        const analyses: AnalysisHistoryItem[] = (data || []).map((item) => ({
          id: item.id,
          status: item.disease === "Healthy Crop" ? "healthy" : "diseased",
          confidence: item.confidence / 100, // Convert from percentage to decimal
          issues: item.symptoms || [],
          recommendations: item.treatment
            ? item.treatment.split(". ").filter(Boolean)
            : [],
          timestamp: new Date(item.created_at),
          image_path: item.image_url,
          provider: "Database",
        }));
        setHistory(analyses);
      }
    } catch (error) {
      console.error("History load error:", error);
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadAnalysisHistory();
    } else {
      setHistory([]);
    }
  }, [user?.id, historyFilter]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const getSeverityColor = (status: string) => {
    switch (status) {
      case "healthy":
        return "text-green-600 bg-green-100";
      case "diseased":
        return "text-red-600 bg-red-100";
      case "pest_detected":
        return "text-orange-600 bg-orange-100";
      case "nutrient_deficiency":
        return "text-yellow-600 bg-yellow-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getSeverityIcon = (status: string) => {
    switch (status) {
      case "healthy":
        return <CheckCircle className="w-4 h-4" />;
      case "diseased":
        return <AlertTriangle className="w-4 h-4" />;
      case "pest_detected":
        return <AlertTriangle className="w-4 h-4" />;
      case "nutrient_deficiency":
        return <Clock className="w-4 h-4" />;
      default:
        return <Leaf className="w-4 h-4" />;
    }
  };

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] bg-white rounded-lg border border-green-200 shadow p-6">
        <Camera className="w-16 h-16 mb-4 text-gray-400" />
        <h2 className="mb-2 text-2xl font-semibold text-gray-700">
          {language === "st"
            ? "Tlhahlobo ea Bophelo ba Lijalo"
            : "Crop Health Analysis"}
        </h2>
        <p className="mb-6 text-center text-gray-500 max-w-md">
          {language === "st"
            ? "Kena ho fumana tlhahlobo ea AI ea mafu a lijalo le tlhokomelo ea bophelo"
            : "Sign in to access AI-powered crop disease detection and health monitoring"}
        </p>
        <button
          onClick={onSignIn}
          className="px-6 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700"
        >
          {language === "st"
            ? "Kena ho Hlahloba Lijalo"
            : "Sign In to Analyze Crops"}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Crop Analysis Section - Using same design as modal */}
      <div className="bg-white rounded-lg border border-green-200 shadow p-6">
        <div className="flex items-center gap-3 pb-4 mb-6 border-b border-gray-200">
          <Camera className="text-green-600 w-9 h-9" />
          <h1 className="text-3xl font-extrabold text-gray-800">
            {language === "st" ? "Tlhahlobo ea Lijalo" : "Crop Analysis"}
          </h1>
        </div>

        <p className="mb-6 text-base leading-relaxed text-gray-600">
          <span className="font-semibold text-green-700">
            {language === "st"
              ? "Lijalo tse tšehetsoang: Poone (Maize), Litapole, Tamati, Pelepele, Soybean."
              : "Supported crops: Corn (Maize), Potato, Tomato, Pepper, Soybean."}
          </span>
          <br />
          {language === "st"
            ? "Nka setšoantšo kapa jarisa setšoantšo se hlakileng sa makhasi bakeng sa tlhahlobo ea AI le likeletso."
            : "Take a photo or upload a clear leaf image for AI-based analysis and recommendations."}
        </p>

        {/* Tab switcher */}
        <div className="flex mb-4 space-x-2">
          <button
            type="button"
            className={`px-3 py-2 rounded-lg font-medium text-sm ${
              tab === "upload"
                ? "bg-green-600 text-white"
                : "bg-gray-100 text-green-800"
            }`}
            onClick={() => setTab("upload")}
          >
            {language === "st" ? "Jarisa" : "Upload"}
          </button>
          <button
            type="button"
            className={`px-3 py-2 rounded-lg font-medium text-sm ${
              tab === "camera"
                ? "bg-green-600 text-white"
                : "bg-gray-100 text-green-800"
            }`}
            onClick={() => setTab("camera")}
          >
            {language === "st" ? "Khamera" : "Camera"}
          </button>
        </div>

        {/* Upload Tab */}
        {tab === "upload" && (
          <label
            htmlFor="file-upload"
            className={`group flex flex-col items-center justify-center w-full min-h-[150px] border-2 ${
              previewUrl ? "border-green-500" : "border-gray-300"
            } border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors duration-300 ease-in-out
            hover:border-green-600 hover:bg-green-50`}
          >
            <input
              id="file-upload"
              type="file"
              accept="image/*"
              className="hidden"
              disabled={loading}
              onChange={handleFileChange}
              ref={fileInputRef}
            />
            <div className="flex flex-col items-center justify-center space-y-3">
              {loading ? (
                <Loader2 className="w-10 h-10 text-green-500 animate-spin" />
              ) : (
                <Upload className="w-10 h-10 text-gray-400 group-hover:text-green-600" />
              )}
              <p className="text-lg font-medium text-gray-700">
                {image
                  ? image.name
                  : language === "st"
                  ? "Hula & Lahla kapa Tobetsa ho Jarisa"
                  : "Drag & Drop or Click to Upload"}
              </p>
              <p className="text-sm text-gray-500">
                {image
                  ? language === "st"
                    ? "Fetola setšoantšo"
                    : "Change image"
                  : language === "st"
                  ? "PNG, JPG, GIF ho fihla ho 5MB"
                  : "PNG, JPG, GIF up to 5MB"}
              </p>
            </div>
          </label>
        )}

        {/* Camera Tab */}
        {tab === "camera" && (
          <div className="flex flex-col items-center justify-center w-full">
            {cameraError ? (
              <div className="w-full p-4 text-center text-red-700 bg-red-100 border border-red-400 rounded-lg">
                {cameraError}
              </div>
            ) : (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full max-w-xs mb-4 border border-gray-300 rounded-lg shadow-md aspect-video"
                />
                {!cameraActive ? (
                  <button
                    onClick={startCamera}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-blue-600 text-white font-bold shadow hover:bg-blue-700 active:scale-95"
                  >
                    <Camera className="w-5 h-5" />
                    {language === "st" ? "Bulela Khamera" : "Start Camera"}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleTakePhoto}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-green-600 text-white font-bold shadow hover:bg-green-700 active:scale-95"
                    disabled={!cameraActive}
                  >
                    <Camera className="w-5 h-5" />
                    {language === "st" ? "Nka Setšoantšo" : "Take Photo"}
                  </button>
                )}
                <canvas ref={canvasRef} className="hidden" />
              </>
            )}
          </div>
        )}

        {/* Image Preview and Analyze Button */}
        {previewUrl && (
          <div className="flex flex-col items-center p-5 mt-6 border border-gray-200 shadow-inner bg-gray-50 rounded-xl">
            <img
              src={previewUrl}
              alt="Crop Leaf Preview"
              className="object-contain mb-5 border border-gray-300 rounded-lg shadow-md max-h-64"
            />
            <button
              type="button"
              onClick={performAnalysis}
              disabled={Boolean(loading || !user || !user.id || !image)}
              className={`flex items-center justify-center gap-3 px-8 py-3 rounded-full font-bold text-white uppercase tracking-wider transition-all duration-300 ease-in-out
                ${
                  loading || !user || !user.id || !image
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 active:scale-95 shadow-lg hover:shadow-xl"
                }`}
            >
              {loading && <Loader2 className="w-6 h-6 animate-spin" />}
              {loading
                ? language === "st"
                  ? "E ntse e hlahloba..."
                  : "Analyzing..."
                : language === "st"
                ? "Hlahloba Setšoantšo"
                : "Analyze Image"}
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div
            className="relative px-5 py-4 mt-6 text-red-700 bg-red-100 border border-red-400 rounded-md animate-fade-in"
            role="alert"
          >
            <strong className="font-bold">Error:</strong>
            <span className="block ml-2 sm:inline">{error}</span>
          </div>
        )}

        {/* Analysis Results */}
        {showResults && analysisResult && (
          <div className="mt-6 p-6 bg-gradient-to-br from-green-50 to-blue-50 border border-green-200 rounded-xl shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-green-800 flex items-center gap-2">
                <Leaf className="w-6 h-6" />
                {language === "st"
                  ? "Sephetho sa Tlhahlobo"
                  : "Analysis Results"}
              </h3>
              <button
                onClick={() => setShowResults(false)}
                className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-white/50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center gap-3">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${getSeverityColor(
                    analysisResult.status
                  )}`}
                >
                  {getSeverityIcon(analysisResult.status)}
                  {analysisResult.status.replace("_", " ").toUpperCase()}
                </span>
                <span className="text-sm text-gray-600">
                  {(analysisResult.confidence * 100).toFixed(1)}%{" "}
                  {language === "st" ? "tšepo" : "confidence"}
                </span>
              </div>

              {/* Issues */}
              {analysisResult.issues && analysisResult.issues.length > 0 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {language === "st" ? "Mathata a Fumaneng" : "Issues Found"}
                  </h4>
                  <ul className="space-y-1">
                    {analysisResult.issues.map(
                      (issue: string, index: number) => (
                        <li
                          key={index}
                          className="text-red-700 text-sm flex items-start gap-2"
                        >
                          <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                          {issue}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              )}

              {/* Recommendations */}
              {analysisResult.recommendations &&
                analysisResult.recommendations.length > 0 && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      {language === "st" ? "Likeletso" : "Recommendations"}
                    </h4>
                    <ul className="space-y-1">
                      {analysisResult.recommendations.map(
                        (rec: string, index: number) => (
                          <li
                            key={index}
                            className="text-green-700 text-sm flex items-start gap-2"
                          >
                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                            {rec}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                )}

              {/* Provider info */}
              {analysisResult.provider && (
                <div className="text-xs text-gray-500 text-center">
                  {language === "st" ? "E hlahlobiloe ke" : "Analyzed by"}{" "}
                  {analysisResult.provider}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Analysis History */}
      <div className="bg-white rounded-lg border border-green-200 shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-green-800 flex items-center gap-2">
            <History className="w-5 h-5" />
            <span>
              {language === "st" ? "Nalane ea Tlhahlobo" : "Analysis History"}
            </span>
          </h3>

          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={historyFilter}
              onChange={(e) => setHistoryFilter(e.target.value as any)}
              className="px-2 py-1 text-sm border border-gray-300 rounded"
            >
              <option value="3">
                {language === "st" ? "Tsa ho qetela 3" : "Last 3"}
              </option>
              <option value="6">
                {language === "st" ? "Tsa ho qetela 6" : "Last 6"}
              </option>
              <option value="9">
                {language === "st" ? "Tsa ho qetela 9" : "Last 9"}
              </option>
              <option value="12">
                {language === "st" ? "Tsa ho qetela 12" : "Last 12"}
              </option>
              <option value="all">
                {language === "st" ? "Tsohle" : "All"}
              </option>
            </select>
          </div>
        </div>

        {loadingHistory ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : history.length > 0 ? (
          <div className="space-y-4">
            {history.map((item) => (
              <div
                key={item.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getSeverityColor(
                          item.status
                        )}`}
                      >
                        {getSeverityIcon(item.status)}
                        {item.status.replace("_", " ").toUpperCase()}
                      </span>
                      <span className="text-xs text-gray-500">
                        {item.timestamp.toLocaleDateString()}
                      </span>
                      <span className="text-xs text-gray-500">
                        {(item.confidence * 100).toFixed(1)}% confidence
                      </span>
                    </div>

                    {item.issues.length > 0 && (
                      <div className="mb-2">
                        <p className="text-sm font-medium text-red-700 mb-1">
                          {language === "st" ? "Mathata:" : "Issues:"}
                        </p>
                        <p className="text-sm text-red-600">
                          {item.issues.join(", ")}
                        </p>
                      </div>
                    )}

                    <div>
                      <p className="text-sm font-medium text-green-700 mb-1">
                        {language === "st" ? "Likeletso:" : "Recommendations:"}
                      </p>
                      <p className="text-sm text-green-600">
                        {item.recommendations.join(", ")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <History className="w-12 h-12 text-gray-400 mx-auto mb-2" />
            <p>
              {language === "st"
                ? "Ha ho nalane ea tlhahlobo haesale."
                : "No analysis history yet."}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {language === "st"
                ? "Litlhahlobo tsa hau tse qetileng li tla hlaha mona."
                : "Your completed analyses will appear here."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
